import { Box, Stack, Typography } from '@mui/material';
import { useUsersCalendarEventsList } from 'djangogen/client';
import { useEffect, useRef, useState } from 'react';

import { getRoomWeekColor } from '../../Utils/calendarColors';
import { dayjs } from '../Dayjs/dayjs-utils';

import {
    clampMinutes,
    EVENT_LINE_HEIGHT,
    FALLBACK_GRID_BODY_HEIGHT_PX,
    formatCompactEventTimeRange,
    getDayColumnData,
    getHourLinePercentage,
    getTimedEventLayout,
    getUniformEventTypography,
    getVisibleTimeLabel,
    HOURS_END,
    HOURS_START,
    TIME_AXIS_WIDTH,
    TOTAL_VISIBLE_MINUTES,
} from './roomCalendarUtils';

import type { Dashboard } from '../../Types/AuthServer';
import type { EventBoxMetrics, EventTypography, PositionedEvent } from './roomCalendarUtils';

const ROOM_HEADER_HEIGHT = 62;

export interface MultiRoomTarget {
    calendarId: string;
    id: string;
    label: string;
}

/** What one column reports upwards, with the signature the view compares to ignore a no-op report. */
interface RoomEventMetricsReport {
    metrics: EventBoxMetrics[];
    signature: string;
}

/**
 * Box geometry of one positioned event. Used twice per column: once to publish the metrics the view
 * sizes its shared typography from, once to paint the box. Both passes must see the same numbers, so
 * this is the only place they are derived.
 */
function getEventBoxMetrics({
    columnBodyHeightPx,
    columnWidthPx,
    event,
}: {
    columnBodyHeightPx: number;
    columnWidthPx: number;
    event: PositionedEvent;
}): EventBoxMetrics & { heightPercentage: number } {
    const durationMinutes = event.endMinutes - event.startMinutes;
    const heightPercentage = Math.max((durationMinutes * 100) / TOTAL_VISIBLE_MINUTES, 3.8);

    return {
        eventHeightPx: (heightPercentage / 100) * columnBodyHeightPx,
        eventWidthPx: Math.max(Math.round(columnWidthPx / Math.max(event.laneCount, 1) - 6), 42),
        heightPercentage,
        label: event.publicLabel,
    };
}

interface InfoscreenMultiRoomCalendarViewProps {
    currentTime: dayjs.Dayjs;
    infoscreenLayout: Dashboard['socials'];
    rooms: readonly MultiRoomTarget[];
    showEventTitles: boolean;
    timezone: string;
}

interface RoomDayColumnProps {
    columnBodyHeightPx: number;
    columnIndex: number;
    columnWidthPx: number;
    currentTime: dayjs.Dayjs;
    day: dayjs.Dayjs;
    isLastColumn: boolean;
    onEventMetricsChange: (roomId: string, metrics: EventBoxMetrics[], signature: string) => void;
    room: MultiRoomTarget;
    roomCount: number;
    showEventTitles: boolean;
    timezone: string;
    typography: EventTypography | undefined;
}

function RoomDayColumn({
    columnBodyHeightPx,
    columnIndex,
    columnWidthPx,
    currentTime,
    day,
    isLastColumn,
    onEventMetricsChange,
    room,
    roomCount,
    showEventTitles,
    timezone,
    typography,
}: Readonly<RoomDayColumnProps>) {
    const { data: events } = useUsersCalendarEventsList(
        {
            calendar: room.calendarId,
            end: day.endOf('day').toISOString(),
            start: day.startOf('day').toISOString(),
        },
        {
            swr: {
                enabled: Boolean(room.calendarId),
                refreshInterval: 60_000,
                revalidateOnFocus: false,
            },
        },
    );

    const { allDayEvents, positionedEvents } = getDayColumnData({
        currentDay: day,
        events: events ?? [],
        showEventTitles,
        timezone,
    });

    const eventBoxMetrics = positionedEvents.map((event) =>
        getEventBoxMetrics({ columnBodyHeightPx, columnWidthPx, event }),
    );
    // Each column fetches its own reservations, so the view can only size one shared typography once
    // every column has reported what it holds. The signature keeps the effect off the identity of a
    // freshly mapped array and lets the parent ignore a report that changes nothing.
    const eventBoxMetricsSignature = JSON.stringify(eventBoxMetrics);

    useEffect(() => {
        onEventMetricsChange(
            room.id,
            JSON.parse(eventBoxMetricsSignature) as EventBoxMetrics[],
            eventBoxMetricsSignature,
        );
    }, [eventBoxMetricsSignature, onEventMetricsChange, room.id]);

    const roomColor = getRoomWeekColor(columnIndex);
    const isToday = day.isSame(currentTime, 'day');
    const nowLineVisible = isToday && currentTime.hour() >= HOURS_START;
    const currentLineTop = clampMinutes(
        currentTime.diff(day.hour(HOURS_START).minute(0).second(0).millisecond(0), 'minute'),
    );
    const gridColumn = `${(columnIndex + 2).toString()} / ${(columnIndex + 3).toString()}`;
    const columnBorderRight = isLastColumn ? 'none' : '1px solid rgba(255,255,255,0.08)';

    return (
        <>
            <Stack
                sx={{
                    backgroundColor: 'rgba(255,255,255,0.04)',
                    borderBottom: '1px solid rgba(255,255,255,0.12)',
                    borderRight: columnBorderRight,
                    gridColumn,
                    gridRow: '1 / 2',
                    justifyContent: 'center',
                    minWidth: 0,
                    px: 0.75,
                }}
            >
                <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                    {/* Six columns at 960px leave the name room to ellipsise, so the color is the
                        second identity signal — and the same one the week view gives this room. */}
                    <Box
                        sx={{
                            backgroundColor: roomColor,
                            borderRadius: '50%',
                            flexShrink: 0,
                            height: 9,
                            width: 9,
                        }}
                    />
                    <Typography noWrap sx={{ fontSize: 'clamp(12px, 1.1vw, 18px)', fontWeight: 700 }}>
                        {room.label}
                    </Typography>
                </Stack>
                <Typography noWrap sx={{ fontSize: 'clamp(9px, 0.7vw, 12px)', opacity: 0.7 }}>
                    {allDayEvents.length > 0 ? allDayEvents.join(' · ') : 'No all-day bookings'}
                </Typography>
            </Stack>

            <Box
                sx={{
                    backgroundImage: `repeating-linear-gradient(to bottom, transparent, transparent calc((100% / ${(HOURS_END - HOURS_START).toString()}) - 1px), rgba(255,255,255,0.08) calc((100% / ${(HOURS_END - HOURS_START).toString()}) - 1px), rgba(255,255,255,0.08) calc(100% / ${(HOURS_END - HOURS_START).toString()}))`,
                    borderRight: columnBorderRight,
                    gridColumn,
                    gridRow: '2 / 3',
                    minWidth: 0,
                    position: 'relative',
                }}
            >
                {positionedEvents.map((event, eventIndex) => {
                    const top = (event.startMinutes * 100) / TOTAL_VISIBLE_MINUTES;
                    const width = `calc(${(100 / event.laneCount).toString()}% - 6px)`;
                    const left = `calc(${((100 / event.laneCount) * event.lane).toString()}% + 3px)`;
                    const { eventHeightPx, eventWidthPx, heightPercentage } =
                        eventBoxMetrics[eventIndex] ??
                        getEventBoxMetrics({ columnBodyHeightPx, columnWidthPx, event });
                    const timedEventLayout = getTimedEventLayout({
                        eventHeightPx,
                        eventWidthPx,
                        label: event.publicLabel,
                        typography,
                    });
                    const dayVisibleStart = day.hour(HOURS_START).minute(0).second(0).millisecond(0);
                    const eventStart = dayVisibleStart.add(event.startMinutes, 'minute');
                    const timeLabel = formatCompactEventTimeRange(
                        eventStart,
                        dayVisibleStart.add(event.endMinutes, 'minute'),
                    );

                    return (
                        <Box
                            key={event.id}
                            sx={{
                                backgroundColor: roomColor,
                                border: '1px solid rgba(0,0,0,0.18)',
                                borderRadius: 1,
                                boxShadow: '0 4px 8px rgba(0,0,0,0.18)',
                                height: `${heightPercentage.toString()}%`,
                                left,
                                // Grow to exactly what the text paints, never to an estimated worst
                                // case, so a short booking stays as close to its own slot height as
                                // its content allows.
                                minHeight: 'fit-content',
                                overflow: 'hidden',
                                position: 'absolute',
                                top: `${top.toString()}%`,
                                width,
                                zIndex: 2,
                            }}
                        >
                            <Box
                                sx={{
                                    boxSizing: 'border-box',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: `${timedEventLayout.gapPx.toString()}px`,
                                    minWidth: 0,
                                    px: 0.4,
                                    py: 0.25,
                                    width: '100%',
                                }}
                            >
                                <Typography
                                    sx={{
                                        display: '-webkit-box',
                                        fontSize: `${timedEventLayout.labelFontSizePx.toString()}px !important`,
                                        fontWeight: 700,
                                        lineHeight: EVENT_LINE_HEIGHT,
                                        minWidth: 0,
                                        overflow: 'hidden',
                                        overflowWrap: 'anywhere',
                                        WebkitBoxOrient: 'vertical',
                                        WebkitLineClamp: timedEventLayout.labelLineClamp,
                                    }}
                                >
                                    {event.publicLabel}
                                </Typography>
                                <Typography
                                    sx={{
                                        display: '-webkit-box',
                                        flexShrink: 0,
                                        fontSize: `${timedEventLayout.timeFontSizePx.toString()}px !important`,
                                        fontWeight: 600,
                                        lineHeight: EVENT_LINE_HEIGHT,
                                        opacity: 0.9,
                                        // Narrow lanes break the range after the hyphen instead of
                                        // ellipsing the end time away.
                                        overflow: 'hidden',
                                        overflowWrap: 'anywhere',
                                        WebkitBoxOrient: 'vertical',
                                        WebkitLineClamp: timedEventLayout.timeLineCount,
                                    }}
                                >
                                    {timeLabel}
                                </Typography>
                            </Box>
                        </Box>
                    );
                })}

                {nowLineVisible && currentLineTop <= TOTAL_VISIBLE_MINUTES ? (
                    <Box
                        sx={{
                            borderTop: '1px solid #ff6b6b',
                            boxShadow: '0 0 3px rgba(0, 0, 0, 0.7)',
                            left: 0,
                            opacity: 0.9,
                            position: 'absolute',
                            right: 0,
                            top: `${((currentLineTop * 100) / TOTAL_VISIBLE_MINUTES).toString()}%`,
                            zIndex: 4,
                        }}
                    />
                ) : null}

                {roomCount > 0 && positionedEvents.length === 0 ? (
                    <Typography
                        sx={{
                            fontSize: 'clamp(10px, 0.8vw, 14px)',
                            left: 0,
                            opacity: 0.45,
                            position: 'absolute',
                            right: 0,
                            textAlign: 'center',
                            top: '45%',
                        }}
                    >
                        Free all day
                    </Typography>
                ) : null}
            </Box>
        </>
    );
}

export default function InfoscreenMultiRoomCalendarView({
    currentTime,
    infoscreenLayout,
    rooms,
    showEventTitles,
    timezone,
}: Readonly<InfoscreenMultiRoomCalendarViewProps>) {
    const calendarGridRef = useRef<HTMLDivElement | null>(null);
    const [calendarGridWidthPx, setCalendarGridWidthPx] = useState(0);
    const [calendarGridHeightPx, setCalendarGridHeightPx] = useState(0);
    const [roomEventMetrics, setRoomEventMetrics] = useState<Record<string, RoomEventMetricsReport>>({});

    const day = currentTime.startOf('day');
    const roomCount = Math.max(rooms.length, 1);
    // Columns share the space left over from the time axis / room headers; used to size event labels.
    const columnWidthPx = Math.max((calendarGridWidthPx - TIME_AXIS_WIDTH) / roomCount, 60);
    const measuredColumnBodyHeightPx = calendarGridHeightPx - ROOM_HEADER_HEIGHT;
    const columnBodyHeightPx =
        measuredColumnBodyHeightPx > 0 ? measuredColumnBodyHeightPx : FALLBACK_GRID_BODY_HEIGHT_PX;

    useEffect(() => {
        const gridElement = calendarGridRef.current;

        if (!gridElement) {
            return;
        }

        const measureGrid = () => {
            const nextGridWidthPx = Math.floor(gridElement.clientWidth);
            const nextGridHeightPx = Math.floor(gridElement.clientHeight);

            setCalendarGridWidthPx((currentWidth) =>
                currentWidth === nextGridWidthPx ? currentWidth : nextGridWidthPx,
            );
            setCalendarGridHeightPx((currentHeight) =>
                currentHeight === nextGridHeightPx ? currentHeight : nextGridHeightPx,
            );
        };

        measureGrid();

        let observer: ResizeObserver | null = null;

        if (typeof ResizeObserver !== 'undefined') {
            observer = new ResizeObserver(() => {
                measureGrid();
            });
            observer.observe(gridElement);
        }

        return () => {
            if (observer) {
                observer.disconnect();
            }
        };
    }, [roomCount]);

    const handleRoomEventMetricsChange = (roomId: string, metrics: EventBoxMetrics[], signature: string) => {
        setRoomEventMetrics((currentMetrics) =>
            currentMetrics[roomId]?.signature === signature
                ? currentMetrics
                : { ...currentMetrics, [roomId]: { metrics, signature } },
        );
    };

    // One title size and one time size for the whole day, not per box: six columns of reservations
    // sit side by side on the wall, and a short booking sized to its own box next to a long one reads
    // as a rendering fault. Stale keys from rooms no longer selected are dropped by reading through
    // the current room list. Undefined until the first column reports, so the first paint sizes each
    // box on its own and the second settles the view on one size.
    const allEventMetrics = rooms.flatMap((room) => roomEventMetrics[room.id]?.metrics ?? []);
    const eventTypography = allEventMetrics.length > 0 ? getUniformEventTypography(allEventMetrics) : undefined;

    return (
        <Stack
            sx={{
                backgroundColor: infoscreenLayout?.backgroundColor || '#000000',
                boxSizing: 'border-box',
                color: infoscreenLayout?.textColor || '#ffffff',
                gap: 0.75,
                height: '100%',
                px: { sm: 1.5, xs: 1 },
                py: { sm: 1.25, xs: 1 },
                width: '100%',
            }}
        >
            <Stack direction="row" sx={{ alignItems: 'flex-start', gap: 2, justifyContent: 'space-between', pr: '104px' }}>
                <Box>
                    <Typography sx={{ fontSize: 'clamp(18px, 1.8vw, 28px)', fontWeight: 700 }}>
                        Room Reservations
                    </Typography>
                    <Typography sx={{ fontSize: 'clamp(11px, 0.9vw, 15px)', opacity: 0.75 }}>
                        {`${day.format('dddd D.M.YYYY')} · ${rooms.length.toString()} ${rooms.length === 1 ? 'room' : 'rooms'}`}
                    </Typography>
                </Box>
                <Typography sx={{ fontSize: 'clamp(13px, 1vw, 17px)', opacity: 0.9, pt: 0.35, textAlign: 'right' }}>
                    {currentTime.format('ddd D.M. HH:mm')}
                </Typography>
            </Stack>

            <Box
                ref={calendarGridRef}
                sx={{
                    border: '1px solid rgba(255,255,255,0.14)',
                    borderRadius: 1.5,
                    display: 'grid',
                    flex: 1,
                    gridTemplateColumns: `${TIME_AXIS_WIDTH.toString()}px repeat(${roomCount.toString()}, minmax(0, 1fr))`,
                    gridTemplateRows: `${ROOM_HEADER_HEIGHT.toString()}px minmax(0, 1fr)`,
                    minHeight: 0,
                    overflow: 'hidden',
                }}
            >
                <Box
                    sx={{
                        backgroundColor: 'rgba(255,255,255,0.04)',
                        borderBottom: '1px solid rgba(255,255,255,0.12)',
                        borderRight: '1px solid rgba(255,255,255,0.12)',
                        gridColumn: '1 / 2',
                        gridRow: '1 / 2',
                    }}
                />

                <Box
                    sx={{
                        backgroundColor: 'rgba(255,255,255,0.03)',
                        borderRight: '1px solid rgba(255,255,255,0.12)',
                        gridColumn: '1 / 2',
                        gridRow: '2 / 3',
                        position: 'relative',
                    }}
                >
                    {Array.from({ length: HOURS_END - HOURS_START + 1 }, (_, index) => HOURS_START + index).map(
                        (hour) => {
                            const isFirstHour = hour === HOURS_START;
                            const isLastHour = hour === HOURS_END;

                            return (
                                <Typography
                                    key={`hour-${hour.toString()}`}
                                    sx={{
                                        bottom: isLastHour ? 0 : 'auto',
                                        fontSize: '11px',
                                        letterSpacing: '0.04em',
                                        lineHeight: 1,
                                        opacity: 0.8,
                                        position: 'absolute',
                                        right: 8,
                                        top: isLastHour ? 'auto' : `${getHourLinePercentage(hour).toString()}%`,
                                        transform: isFirstHour || isLastHour ? 'none' : 'translateY(-50%)',
                                        zIndex: 2,
                                    }}
                                >
                                    {getVisibleTimeLabel(hour)}
                                </Typography>
                            );
                        },
                    )}
                </Box>

                {rooms.map((room, index) => (
                    <RoomDayColumn
                        columnBodyHeightPx={columnBodyHeightPx}
                        columnIndex={index}
                        columnWidthPx={columnWidthPx}
                        currentTime={currentTime}
                        day={day}
                        isLastColumn={index === rooms.length - 1}
                        key={room.id}
                        onEventMetricsChange={handleRoomEventMetricsChange}
                        room={room}
                        roomCount={rooms.length}
                        showEventTitles={showEventTitles}
                        timezone={timezone}
                        typography={eventTypography}
                    />
                ))}
            </Box>
        </Stack>
    );
}
